import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Button } from './ui/button';
import { Bell, Check, Calendar, Star, XCircle, MessageCircle, CheckCircle, Settings } from 'lucide-react';
import NotificationPreferences from './NotificationPreferences';

const NOTIF_ICONS = {
  new_booking: Calendar,
  booking_confirmed: CheckCircle,
  booking_completed: Check,
  booking_cancelled: XCircle,
  new_review: Star,
  review_response: MessageCircle,
};

const NOTIF_COLORS = {
  new_booking: 'text-blue-500 bg-blue-50',
  booking_confirmed: 'text-green-500 bg-green-50',
  booking_completed: 'text-emerald-500 bg-emerald-50',
  booking_cancelled: 'text-red-500 bg-red-50',
  new_review: 'text-amber-500 bg-amber-50',
  review_response: 'text-purple-500 bg-purple-50',
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch (err) { console.error('Notification count fetch failed:', err); }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications?limit=15');
      setNotifications(res.data);
    } catch (err) { console.error('Notifications fetch failed:', err); }
    finally { setLoading(false); }
  }, []);

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      setShowPrefs(false);
      fetchNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { console.error('Mark all read failed:', err); }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.read) {
      try {
        await api.put(`/notifications/${notif.notification_id}/read`);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
      } catch (err) { console.error('Mark read failed:', err); }
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg text-stone-600 hover:text-[#1A1A1A] hover:bg-stone-100 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed sm:absolute right-4 sm:right-0 top-16 sm:top-full sm:mt-2 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] max-h-[calc(100vh-5rem)] sm:max-h-[520px] bg-white rounded-xl shadow-xl border border-[#E6E4DD] overflow-hidden z-50"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E4DD] bg-[#F9F6F0]">
            <h3 className="font-heading font-medium text-[#1A1A1A]">
              {showPrefs ? 'Settings' : 'Notifications'}
            </h3>
            <div className="flex items-center gap-2">
              {!showPrefs && unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-[#D4AF37] hover:underline font-medium"
                  data-testid="mark-all-read-btn"
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => setShowPrefs(!showPrefs)}
                className={`p-1.5 rounded-md transition-colors ${showPrefs ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}
                data-testid="notification-settings-btn"
                title="Notification settings"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[440px]">
            {showPrefs ? (
              <div className="p-4">
                <NotificationPreferences onClose={() => setShowPrefs(false)} />
              </div>
            ) : loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#D4AF37]" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center" data-testid="no-notifications">
                <Bell className="h-8 w-8 text-stone-300 mx-auto mb-2" />
                <p className="text-sm text-stone-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map(notif => {
                const IconComponent = NOTIF_ICONS[notif.type] || Bell;
                const colorClass = NOTIF_COLORS[notif.type] || 'text-stone-500 bg-stone-50';
                return (
                  <button
                    key={notif.notification_id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#F9F6F0] transition-colors border-b border-[#E6E4DD]/50 ${
                      !notif.read ? 'bg-amber-50/30' : ''
                    }`}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${colorClass}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-snug ${!notif.read ? 'font-semibold text-[#1A1A1A]' : 'text-stone-700'}`}>
                          {notif.title}
                        </p>
                        {!notif.read && (
                          <span className="w-2 h-2 rounded-full bg-[#D4AF37] flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[11px] text-stone-400 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
